import {vatLabelToPercent} from "@/helpers/vat";
import {productGroupIdOf} from "@/helpers/catalog";

const LIST_SEPARATOR = ', ';
export const GROUP_PATH_SEPARATOR = ' > ';
export const COLOR_EXAMPLE = '#8393F4';

const CHANNELS = [
    {source: 'APP', suffix: 'App'},
    {source: 'WEB', suffix: 'Web'},
    {source: 'KIOSK', suffix: 'Kiosk'},
];

function byOrderIndex(a, b) {
    return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
}

export function groupPath(groupId, groupsById, fallbackName = '') {
    const names = [];
    const seen = new Set();
    let group = groupsById.get(groupId);
    while (group && !seen.has(group.productGroupId)) {
        seen.add(group.productGroupId);
        names.unshift(group.name);
        group = groupsById.get(group.parentGroupId);
    }
    return names.length ? names.join(GROUP_PATH_SEPARATOR) : fallbackName;
}

function vatOf(entity, field, blackbox) {
    return blackbox ? vatLabelToPercent(entity[`vatLabel${field}`]) : entity[`vat${field}`];
}

function sortedLinks(product) {
    return [...(product.allowedOptionLists || [])].sort(byOrderIndex);
}

function linkedLists(product, optionListsById) {
    return sortedLinks(product)
        .map(link => optionListsById.get(link.optionListId))
        .filter(Boolean);
}

function withCitedProducts(products, allProducts, optionListsById) {
    const productsById = new Map(allProducts.map(product => [product.productId, product]));
    const exported = new Map(products.map(product => [product.productId, product]));
    const pending = [...exported.values()];
    while (pending.length) {
        linkedLists(pending.pop(), optionListsById).forEach(optionList => {
            (optionList.items || []).forEach(item => {
                const cited = productsById.get(item.productId);
                if (!cited || exported.has(cited.productId)) return;
                exported.set(cited.productId, cited);
                pending.push(cited);
            });
        });
    }
    return [...exported.values()];
}

function withAncestors(groupIds, groupsById) {
    const result = new Set();
    groupIds.forEach(groupId => {
        let group = groupsById.get(groupId);
        while (group && !result.has(group.productGroupId)) {
            result.add(group.productGroupId);
            group = groupsById.get(group.parentGroupId);
        }
    });
    return result;
}

function groupsInTreeOrder(groups) {
    const ids = new Set(groups.map(group => group.productGroupId));
    const childrenByParent = new Map();
    groups.forEach(group => {
        const parentId = ids.has(group.parentGroupId) ? group.parentGroupId : null;
        childrenByParent.set(parentId, [...(childrenByParent.get(parentId) || []), group]);
    });
    const ordered = [];
    const visit = parentId => {
        [...(childrenByParent.get(parentId) || [])].sort(byOrderIndex).forEach(group => {
            ordered.push(group);
            visit(group.productGroupId);
        });
    };
    visit(null);
    return ordered;
}

function uniqueListNames(optionLists) {
    const used = new Map();
    return new Map(optionLists.map(optionList => {
        const base = optionList.displayName || String(optionList.optionListId);
        const count = (used.get(base) || 0) + 1;
        used.set(base, count);
        return [optionList.optionListId, count > 1 ? `${base} (${count})` : base];
    }));
}

function optionGroupPaths(product, optionType, groupsById) {
    return (product.allowedOptionGroups || [])
        .filter(option => option.optionType === optionType)
        .map(option => groupPath(option.productGroup?.productGroupId, groupsById, option.productGroup?.name))
        .filter(Boolean)
        .join(LIST_SEPARATOR);
}

function priceColumns(priceOverride) {
    if (!priceOverride) return {};
    const entries = CHANNELS.map(channel => priceOverride[channel.source] || null);
    const [first] = entries;
    const allEqual = entries.every(entry => entry
        && entry.priceVatInclIn === first.priceVatInclIn
        && entry.priceVatInclOut === first.priceVatInclOut);
    if (allEqual) {
        return {priceIn: first.priceVatInclIn, priceOut: first.priceVatInclOut};
    }
    return Object.fromEntries(CHANNELS.flatMap((channel, index) => [
        [`price${channel.suffix}In`, entries[index]?.priceVatInclIn],
        [`price${channel.suffix}Out`, entries[index]?.priceVatInclOut],
    ]));
}

function productRow(product, {groupsById, listNames, blackbox}) {
    return {
        name: product.name,
        group: groupPath(productGroupIdOf(product), groupsById, product.productGroup?.name),
        priceVatInclIn: product.priceVatInclIn,
        priceVatInclOut: product.priceVatInclOut,
        vatIn: vatOf(product, 'In', blackbox),
        vatOut: vatOf(product, 'Out', blackbox),
        barcodes: (product.productBarcodes || []).join(LIST_SEPARATOR),
        descriptionFr: product.description?.fr,
        descriptionNl: product.description?.nl,
        descriptionEn: product.description?.en,
        color: product.color,
        imageUrl: product.imageUrl,
        externalReference: product.externalReference,
        purchasePriceVatExcl: product.purchasePriceVatExcl,
        orderIndex: product.orderIndex,
        userHidden: !!product.userHidden,
        webHidden: !!product.webHidden,
        kioskHidden: !!product.kioskHidden,
        dynamicPrice: !!product.dynamicPrice,
        weightPrice: !!product.weightPrice,
        optionLists: sortedLinks(product)
            .map(link => listNames.get(link.optionListId))
            .filter(Boolean)
            .join(LIST_SEPARATOR),
        freeOptionGroups: optionGroupPaths(product, 'FREE', groupsById),
        paidOptionGroups: optionGroupPaths(product, 'PAID', groupsById),
        productId: product.productId,
        stock: product.stock,
    };
}

function groupRow(group, {groupsById, blackbox}) {
    return {
        path: groupPath(group.productGroupId, groupsById, group.name),
        vatIn: vatOf(group, 'In', blackbox),
        vatOut: vatOf(group, 'Out', blackbox),
        color: group.color,
        orderIndex: group.orderIndex,
        userHidden: !!group.userHidden,
        webHidden: !!group.webHidden,
        kioskHidden: !!group.kioskHidden,
        imageUrl: group.imageUrl,
        externalReference: group.externalReference,
        maxChoiceFree: group.maxChoiceFree,
        maxChoicePaid: group.maxChoicePaid,
        forceChoiceAmountFree: group.forceChoiceAmountFree,
        forceChoiceAmountPaid: group.forceChoiceAmountPaid,
    };
}

function optionListRow(optionList, listNames) {
    return {
        name: listNames.get(optionList.optionListId),
        nameFr: optionList.nameLocalized?.fr,
        nameNl: optionList.nameLocalized?.nl,
        nameEn: optionList.nameLocalized?.en,
        descriptionFr: optionList.description?.fr,
        descriptionNl: optionList.description?.nl,
        descriptionEn: optionList.description?.en,
        minChoice: optionList.minChoiceTotal,
        maxChoice: optionList.maxChoiceTotal,
        forceChoice: optionList.forceChoiceTotal,
    };
}

function optionListItemRows(optionList, {listNames, productsById, groupsById}) {
    return [...(optionList.items || [])].sort(byOrderIndex).map(item => {
        const product = productsById.get(item.productId) || item.product || {};
        return {
            list: listNames.get(optionList.optionListId),
            product: product.name,
            productGroup: groupPath(productGroupIdOf(product), groupsById, product.productGroup?.name),
            free: !!item.free,
            without: item.type === 'WITHOUT',
            allowMultipleChoice: !!item.allowMultipleChoice,
            maxChoice: item.maxChoice,
            includeOptionLists: !!item.includeOptionLists,
            ...priceColumns(item.free ? null : item.priceOverride),
        };
    });
}

export function buildCatalogRows({products = [], allProducts = [], groups = [], optionLists = [], includeAll = false, blackbox = false}) {
    const groupsById = new Map(groups.map(group => [group.productGroupId, group]));
    const optionListsById = new Map(optionLists.map(optionList => [optionList.optionListId, optionList]));
    const productsById = new Map(allProducts.map(product => [product.productId, product]));

    const exportedProducts = includeAll ? allProducts : withCitedProducts(products, allProducts, optionListsById);

    const exportedLists = includeAll
        ? optionLists
        : [...new Map(exportedProducts
            .flatMap(product => linkedLists(product, optionListsById))
            .map(optionList => [optionList.optionListId, optionList])).values()];

    const exportedGroupIds = includeAll
        ? new Set(groups.map(group => group.productGroupId))
        : withAncestors([
            ...exportedProducts.map(product => productGroupIdOf(product)),
            ...exportedProducts.flatMap(product => (product.allowedOptionGroups || []).map(option => option.productGroup?.productGroupId)),
        ], groupsById);
    const exportedGroups = groupsInTreeOrder(groups.filter(group => exportedGroupIds.has(group.productGroupId)));

    const groupRank = new Map(exportedGroups.map((group, index) => [group.productGroupId, index]));
    const sortedProducts = [...exportedProducts].sort((a, b) =>
        (groupRank.get(productGroupIdOf(a)) ?? Infinity) - (groupRank.get(productGroupIdOf(b)) ?? Infinity)
        || byOrderIndex(a, b));

    const listNames = uniqueListNames(optionLists);

    return {
        products: sortedProducts.map(product => productRow(product, {groupsById, listNames, blackbox})),
        groups: exportedGroups.map(group => groupRow(group, {groupsById, blackbox})),
        optionLists: exportedLists.map(optionList => optionListRow(optionList, listNames)),
        optionListItems: exportedLists.flatMap(optionList => optionListItemRows(optionList, {listNames, productsById, groupsById})),
    };
}

export function buildTemplateRows(t, locale) {
    const example = key => t(`catalogWorkbook.examples.${key}`);
    const descriptionKey = `description${locale.charAt(0).toUpperCase()}${locale.slice(1)}`;
    const dishes = example('dishes');
    const pizzas = [dishes, example('pizzas')].join(GROUP_PATH_SEPARATOR);
    const extras = example('extras');
    const drinks = example('drinks');
    const listName = example('listName');
    return {
        groups: [
            {path: dishes, vatIn: 6, vatOut: 12, orderIndex: 1},
            {path: pizzas, vatIn: 6, vatOut: 12, color: COLOR_EXAMPLE, orderIndex: 1},
            {path: extras, vatIn: 6, vatOut: 6, orderIndex: 2},
            {path: drinks, vatIn: 21, vatOut: 21, orderIndex: 3},
        ],
        products: [
            {
                name: example('pizzaName'),
                group: pizzas,
                priceVatInclIn: 11.5,
                priceVatInclOut: 12.5,
                vatIn: 6,
                vatOut: 12,
                barcodes: ['5410000000017', '5410000000024'].join(LIST_SEPARATOR),
                [descriptionKey]: example('pizzaDescription'),
                color: COLOR_EXAMPLE,
                externalReference: 'PIZ-001',
                purchasePriceVatExcl: 4.2,
                orderIndex: 1,
                userHidden: false,
                webHidden: false,
                kioskHidden: false,
                optionLists: listName,
            },
            {name: example('toppingName'), group: extras, priceVatInclIn: 1.5, vatIn: 6},
            {name: example('drinkName'), group: drinks, priceVatInclIn: 2.8, vatIn: 21},
        ],
        optionLists: [
            {name: listName, [descriptionKey]: example('listDescription'), minChoice: 0, maxChoice: 3},
        ],
        optionListItems: [
            {list: listName, product: example('toppingName'), productGroup: extras, free: false, maxChoice: 2, priceIn: 1, priceOut: 1},
            {list: listName, product: example('toppingName'), productGroup: extras, free: true, without: true},
        ],
    };
}
